import React, { useState } from 'react';
import { Typography, Tooltip, SxProps, Theme } from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { useToast } from '../../context/ToastContext';

interface CopyableTextProps {
  text: string;
  label?: string;
  variant?: 'body2' | 'caption' | 'body1' | 'h6' | 'subtitle1' | 'subtitle2';
  color?: string;
  showIcon?: boolean;
  tooltipText?: string;
  copiedTooltipText?: string;
  errorMessage?: string;
  showLabel?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Generic reusable component for displaying text with copy-to-clipboard functionality.
 * 
 * Features:
 * - Click to copy text to clipboard
 * - Dynamic tooltip (shows custom message or "Copied!" after clicking)
 * - Hover effects (color change, underline, pointer cursor)
 * - Optional copy icon indicator
 * - Fully customizable styling
 * 
 * Examples:
 * - Batch IDs: <CopyableText text={batchId} label="Batch ID" />
 * - Workflow IDs: <CopyableText text={workflowId} label="Workflow ID" />
 * - URLs: <CopyableText text={url} label="API Endpoint" />
 * - Keys: <CopyableText text={apiKey} label="API Key" showIcon={false} />
 */
const CopyableText: React.FC<CopyableTextProps> = ({ 
  text,
  label,
  variant = 'body2',
  color = 'text.secondary',
  showIcon = true,
  tooltipText,
  copiedTooltipText = 'Copied!',
  errorMessage,
  showLabel = true,
  sx = {}
}) => {
  const { showToast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    }).catch(() => {
      showToast(errorMessage || `Failed to copy ${label || 'text'}`, 'error');
    });
  };

  const defaultTooltipText = tooltipText || `Click to copy ${label || 'text'}`;

  return (
    <Tooltip 
      title={copied ? copiedTooltipText : defaultTooltipText} 
      arrow
    >
      <Typography 
        variant={variant}
        color={color}
        sx={{ 
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          '&:hover': {
            color: 'primary.main',
            textDecoration: 'underline'
          },
          ...sx
        }}
        onClick={handleCopy}
      >
        {showLabel && label && `${label}: `}{text}
        {showIcon && <CopyIcon sx={{ fontSize: 12 }} />}
      </Typography>
    </Tooltip>
  );
};

export default CopyableText;

